import * as React from 'react';
import SignatureCanvas from 'react-signature-canvas';
import clsx from 'clsx';

import { useFileUpload } from '@/forms/hooks';
import { AuthHttpService } from '@/auth';
import { bytesToSize } from '@/libs';
import { FileWithPreview } from '@/ui-kit/upload-file/types';
import { Button } from '@/ui-kit/button/button';
import { Icon } from '@/ui-kit/icon/icon';
import { RegularModal } from '@/ui-kit/regular-modal/regular-modal';
import { UploadFile } from '@/ui-kit/upload-file/upload-file';
import { SignatureButton } from '@/ui-kit/signature/signature-button/signature-button';
import { SignatureGenerator } from '@/ui-kit/signature/signature-generator/SignatureGenerator';
import { SignatureDrawer } from '@/ui-kit/signature/signature-drawer/signature-drawer';

type ModalType = 'main' | 'upload' | 'draw' | 'transform';

type Action =
  | { type: 'BACK' }
  | { type: 'TO-UPLOAD' }
  | { type: 'TO-DRAW' }
  | { type: 'TO-TRANSFORM' };

const modalReducer = (state: ModalType, action: Action): ModalType => {
  switch (action.type) {
    case 'BACK':
      switch (state) {
        case 'upload':
        case 'draw':
        case 'transform':
          return 'main';
        default:
          return state;
      }

    case 'TO-UPLOAD':
      return 'upload';
    case 'TO-DRAW':
      return 'draw';
    case 'TO-TRANSFORM':
      return 'transform';
  }
};

type ModalProps = {
  title: string;
  id: string;
};

type ModalMap = {
  [key in Exclude<ModalType, 'main'>]: {
    props: ModalProps;
    onConfirm: () => void;
  };
};

type GeneratedCancelTokenSource = ReturnType<
  AuthHttpService['generateCancelTokenSource']
>;

interface SignatureModalProps extends React.HTMLAttributes<HTMLDivElement> {
  isOpen: boolean;
  onClose: () => void;
  onAddSignature: (signature: string) => void;
}

const SignatureModal = React.forwardRef<HTMLDivElement, SignatureModalProps>(
  ({ isOpen, onClose, className, onAddSignature, ...props }, ref) => {
    const [modalType, dispatch] = React.useReducer(modalReducer, 'main');

    const [signature, setSignature] = React.useState<string | undefined>();

    const signatureDrawerRef = React.useRef<SignatureCanvas | null>(null);

    const modalMap: ModalMap = {
      upload: {
        props: {
          title: 'Upload your signature',
          id: 'signing-upload-modal',
        },
        onConfirm: () => {
          if (signature) {
            onAddSignature(signature);
            setSignature(undefined);
            removeAll();
            handleClose();
          }
        },
      },
      draw: {
        props: {
          title: 'Draw your signature',
          id: 'signing-draw-modal',
        },
        onConfirm: () => {
          if (signature) {
            if (signatureDrawerRef.current) {
              signatureDrawerRef.current.clear();
            }
            onAddSignature(signature);
            setSignature(undefined);
            handleClose();
          }
        },
      },
      transform: {
        props: {
          title: 'Write your signature',
          id: 'signing-transform-modal',
        },
        onConfirm: () => {
          if (signature) {
            onAddSignature(signature);
            setSignature(undefined);
            handleClose();
          }
        },
      },
    };

    const handleClose = () => {
      onClose();
      dispatch({ type: 'BACK' });
    };

    const handleSignatureGenerated = (generatedSignature: string) => {
      setSignature(generatedSignature);
    };

    const handleSignatureDrew = (drewSignature: string) => {
      setSignature(drewSignature);
    };

    const containerClasses = clsx('sm__container', className);

    const [cancelTokenList, setCancelTokenList] = React.useState<
      Record<string, GeneratedCancelTokenSource>
    >({});

    const { files, clearFiles, setFile, updateFile, removeFile, removeAll } =
      useFileUpload();

    const removeTokenFromList = (fileName: string) => {
      setCancelTokenList(prevState => {
        const newState = { ...prevState };
        delete newState[fileName];
        return newState;
      });
    };

    const onDeleteUploadedFile = (fileName: string) => () => {
      removeFile(fileName);
    };

    const onAcceptedFiles = async (files: FileWithPreview[]) => {
      clearFiles();

      files.map(file => {
        setFile({
          name: file.name,
          preview: file.preview,
          loaded: bytesToSize(0),
          total: bytesToSize(file.size),
          progress: 0,
        });

        const progress = 100;

        updateFile({
          name: file.name,
          loaded: bytesToSize(file.size),
          total: bytesToSize(file.size),
          progress,
        });
      });
    };

    const onCancelUploadingFile = (fileName: string) => () => {
      const cancelToken = cancelTokenList[fileName];

      if (cancelToken) {
        cancelToken.cancel();
        removeTokenFromList(fileName);
      }

      removeFile(fileName);
    };

    const onDeleteUploadingFile = (fileName: string) => () => {
      removeFile(fileName);
    };

    const onDeleteAllUploadingFiles = () => {
      Object.keys(cancelTokenList).forEach(fileName => {
        const cancelToken = cancelTokenList[fileName];
        cancelToken.cancel();
      });
      setCancelTokenList({});
      removeAll();
    };

    React.useEffect(() => {
      if (Object.values(files.uploaded).length > 0) {
        for (const file of Object.values(files.uploaded)) {
          setSignature(file.preview);
          break;
        }
      }
    }, [files.uploaded]);

    return (
      <div ref={ref} className={containerClasses} {...props}>
        {modalType === 'main' && (
          <RegularModal
            title="Select method for signing"
            id="signing-main-modal"
            size="large"
            isOpen={isOpen}
            onClose={onClose}
            buttons={{
              variant: 'signature',
              list: [
                <SignatureButton
                  icon={<Icon type="add-image" size="large" />}
                  onClick={() => {
                    dispatch({ type: 'TO-UPLOAD' });
                  }}
                >
                  Upload a photo of your signature
                </SignatureButton>,
                <SignatureButton
                  icon={<Icon type="draw" size="large" />}
                  onClick={() => {
                    dispatch({ type: 'TO-DRAW' });
                  }}
                >
                  Draw you signature
                </SignatureButton>,
                <SignatureButton
                  icon={<Icon type="text" size="large" />}
                  onClick={() => {
                    dispatch({ type: 'TO-TRANSFORM' });
                  }}
                >
                  Transform your name into a signature
                </SignatureButton>,
              ],
            }}
          />
        )}
        {modalType !== 'main' && (
          <RegularModal
            {...modalMap[modalType].props}
            size="large"
            isOpen={isOpen}
            onClose={handleClose}
            backCTA={() => {
              dispatch({ type: 'BACK' });
            }}
            buttons={{
              variant:
                modalType === 'draw' && signature !== undefined
                  ? 'double'
                  : 'single',
              list: [
                ...(modalType === 'draw' && signature !== undefined
                  ? [
                      <Button
                        type="button"
                        size="hero"
                        variant="tertiary"
                        kind="danger"
                        onClick={() => {
                          if (signatureDrawerRef.current) {
                            signatureDrawerRef.current.clear();
                          }
                          setSignature(undefined);
                        }}
                        leadingIcon={<Icon type="trash" size="medium" />}
                      >
                        Clear
                      </Button>,
                    ]
                  : []),
                <Button
                  type="button"
                  size="hero"
                  variant="primary"
                  kind="default"
                  onClick={modalMap[modalType].onConfirm}
                  disabled={signature === undefined}
                >
                  Upload Signature
                </Button>,
              ],
            }}
          >
            {modalType === 'upload' ? (
              <UploadFile
                name="input-signature-upload"
                uploadedFiles={files.uploaded}
                uploadingFiles={files.uploading}
                onDeleteUploadedFile={onDeleteUploadedFile}
                onCancelUploadingFile={onCancelUploadingFile}
                onDeleteUploadingFile={onDeleteUploadingFile}
                onDeleteAllUploadingFiles={onDeleteAllUploadingFiles}
                onAcceptedFiles={onAcceptedFiles}
                includePreview
                accept={{ 'image/png': ['.png'] }}
              />
            ) : modalType === 'draw' ? (
              <SignatureDrawer
                onSignatureDrew={handleSignatureDrew}
                ref={signatureDrawerRef}
              />
            ) : (
              <SignatureGenerator
                onSignatureGenerated={handleSignatureGenerated}
              />
            )}
          </RegularModal>
        )}
      </div>
    );
  },
);

export { SignatureModal };
